import { Input } from '@optools/components/ui/input';
import { cn } from '@optools/components/ui/utils';
import React, { useMemo } from 'react';

export type TimePickerType = 'minutes' | 'seconds' | 'hours';

export interface TimePickerInputProps extends React.ComponentProps<'input'> {
  onFocusLeft?: () => void;
  onFocusRight?: () => void;
  setValue: (value: string, type: TimePickerType) => void;
  time: string;
  timeType: TimePickerType;
}

function calculateStepValue(value: string, step: number) {
  let numericValue = parseInt(value, 10);
  if (!isNaN(numericValue)) {
    numericValue += step;
    return numericValue.toString().padStart(2, '0');
  }

  return '00';
}

const TimePickerInput = ({
  className,
  id,
  name,
  onFocusLeft,
  onFocusRight,
  onKeyDown,
  ref,
  setValue,
  time,
  timeType,
}: TimePickerInputProps) => {
  const [flag, setFlag] = React.useState<boolean>(false);

  /**
   * allow the user to enter the second digit within 2 seconds
   * otherwise start again with entering first digit
   */
  React.useEffect(() => {
    if (flag) {
      const timer = setTimeout(() => {
        setFlag(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [flag]);

  const calculateNewValue = (key: string) => {
    if (!flag) {
      return '0' + key;
    } else {
      return (time.length >= 2 ? time.slice(1) : time) + key;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') return;

    e.preventDefault();

    if (e.key === 'ArrowRight') {
      onFocusRight?.();
    } else if (e.key === 'ArrowLeft') {
      onFocusLeft?.();
    } else if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      const step = e.key === 'ArrowUp' ? 1 : -1;
      void (flag && setFlag(false));
      setValue(calculateStepValue(time, step), timeType);
    } else if (e.key >= '0' && e.key <= '9') {
      const newValue = calculateNewValue(e.key);
      if (flag) onFocusRight?.();
      setFlag((prev) => !prev);
      setValue(newValue, timeType);
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      setFlag(false);
      setValue('00', timeType);
    }
  };

  const displayValue = useMemo(() => time.padStart(2, '0'), [time]);

  return (
    <Input
      ref={ref}
      className={cn('w-12 text-center focus:bg-accent focus:text-accent-foreground', className)}
      id={id}
      inputMode="numeric"
      name={name}
      value={displayValue}
      onChange={(event) => {
        event.preventDefault();
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        handleKeyDown(event);
      }}
    />
  );
};

export { TimePickerInput };
